import { isFunction } from "../../lib/utils";

export const PREFIX = "oui-";

export const excludeCustomFromChildren = children => {
  return (children || []).filter(child => !isFunction(child.renderComponent));
};

/**
 * Get all parent nodes of a single tree node.
 *
 * @param {Object} node The node whose parents need to be returned
 * @param {Array} parents Accumulation of parents from previous calls
 */
export function getAllParentNodes(node, parents = []) {
  if (!node.value) {
    return parents;
  }
  const parentValue = this.nodes[PREFIX + node.value]
    ? this.nodes[PREFIX + node.value].parent
    : "";
  const parentNode = this.nodes[parentValue] || {};
  const childrenPropName = this.getChildrenPropName(parentNode);
  if (parentNode[childrenPropName] && parentNode[childrenPropName].length) {
    return getAllParentNodes.call(this, parentNode, [...parents, parentNode]);
  }
  return parents;
}

/**
 * Get immidiate checked parent node of a tree node. If an array of
 * parents is provided, it will iterate over them for efficiency otherwise
 * it will first get parent nodes and processes further.
 *
 * @param {Array|Object} parents Parents array or tree node
 */
export function getImmediateCheckedParent(parents = []) {
  const parentNodes = Array.isArray(parents)
    ? parents
    : getAllParentNodes.call(this, parents);
  // eslint-disable-next-line no-restricted-syntax
  for (const parent of parentNodes) {
    if (parent.checked) {
      return parent;
    }
  }
  return null;
}

/**
 * Update the status of all children true/false of this node.
 *
 * @param {Object} node Node of which children should be updated
 * @param {Boolean} isChecked Status of children to be marked
 */
export function checkAllImmediateChildren(node, isChecked) {
  const childrenPropName = this.getChildrenPropName(node);
  excludeCustomFromChildren(node[childrenPropName]).forEach(child => {
    if (!child[this.props.disabledProp]) {
      this.toggleNode("checked", child, isChecked);
    }
  });
}

/**
 * Updated the outer heirarchy of this node.
 *
 * @param {Object} node Node of which outer heirarchy should be updated.
 */
export function updateNodeOuterHeirarchy(node) {
  const parents = getAllParentNodes.call(this, node);
  const immediateCheckedParent = getImmediateCheckedParent.call(this, parents);
  const firstParent = parents[0];

  if (immediateCheckedParent !== null) {
    this.toggleNode("checked", immediateCheckedParent, false);
    checkAllImmediateChildren.call(this, immediateCheckedParent, true);

    if (parents.length) {
      parents.forEach(parent => {
        this.toggleNode("checked", parent, false);
      });
      checkAllImmediateChildren.call(this, firstParent, true);
    }
  }
}

/**
 *
 * @param {Object} node
 * @param {*} isChecked
 */
export function updateNodeInnerHeirarchy(node, isChecked) {
  if (this.isChildrenEmpty(node)) {
    if (!node[this.props.disabledProp]) {
      this.toggleNode("checked", node, isChecked);
    }
  } else {
    this.toggleNode("checked", node, isChecked);

    const childrenPropName = this.getChildrenPropName(node);

    excludeCustomFromChildren(node[childrenPropName]).forEach(child => {
      updateNodeInnerHeirarchy.call(this, child, false);
    });
  }
}

/**
 * Check if any parent of this node is checked
 *
 * @param {Object} node Node object
 */
export function isAnyParentChecked(node) {
  if (!node.value) {
    return false;
  }
  const parentValue = this.nodes[PREFIX + node.value]
    ? this.nodes[PREFIX + node.value].parent
    : "";
  const parentNode = this.nodes[parentValue] || {};
  const childrenPropName = this.getChildrenPropName(parentNode);
  if (parentNode[childrenPropName] && parentNode[childrenPropName].length) {
    if (parentNode.checked) {
      return true;
    }
    return isAnyParentChecked.call(this, parentNode);
  }
  return false;
}

/**
 * Check if any child of this node is checked
 *
 * @param {Object} node Node object
 */
export function isAnyChildChecked(node) {
  const { uniqueDataKey } = this.props;
  const childrenPropName = this.getChildrenPropName(node);

  return excludeCustomFromChildren(node[childrenPropName]).some(child => {
    if (this.nodes[PREFIX + child[uniqueDataKey]].checked) {
      return true;
    }
    if (!this.isChildrenEmpty(child)) {
      return isAnyChildChecked.call(this, child);
    }
    return (
      this.nodes[PREFIX + child[uniqueDataKey]].checked ||
      this.nodes[PREFIX + child[uniqueDataKey]].halfChecked
    );
  });
}

/**
 * Get current checked state of a node
 *
 * @param {Object} node Node of which state has to be checked
 */
export function getOptimisticCheckState(node) {
  const { checkable, checked, uniqueDataKey } = this.props;

  if (!checkable) {
    return checked.includes(node[uniqueDataKey]) ? 1 : 0;
  }

  if (node.checked) {
    return 1;
  }
  if (isAnyParentChecked.call(this, node) && !node[this.props.disabledProp]) {
    return 1;
  }
  if (isAnyChildChecked.call(this, node) && !node[this.props.disabledProp]) {
    return 2;
  }

  return 0;
}

/**
 * Toggle node on check
 *
 * @param {Object} node Node to toggle
 * @param {Boolean} isChecked Status of node
 */
export function optimisticToggleCheck(node, isChecked) {
  updateNodeOuterHeirarchy.call(this, node);
  updateNodeInnerHeirarchy.call(this, node, isChecked);
}
